<template>
<div class="hc-stackedarea">
  <svg :style="style" class="hc-stackedarea-chart">
    <path
      v-for="(zone, index) in zones"
      :key="index"
      :d="zone"
      :fill="colorScale(index)"
    />
    <path
      v-for="(line, index) in lines"
      :key="'line-' + index"
      :d="line"
      :stroke="lineColorScale(index)"
      stroke-width="2"
      fill="transparent"
    ></path>
    <text-axis
      :scaleFunction="pointScale"
      :scale="scale"
      :height="height * .85"
      :offset="horizentalAxisOffset"
      color="#666"
      :data="data"
    />
    <vertical-axis
      :scaleFunction="scaleLinearY"
      :scale="scale"
      :width="width * .95"
      :offset="verticalAxisOffset"
      color="#666"
      :legends="legends"
    />
  </svg>
</div>
</template>

<script>
import TextAxis from '../libraries/TextAxis'
import VerticalAxis from '../libraries/VerticalAxis'
import { scalePoint, scaleOrdinal, scaleLinear } from 'd3-scale'
import { area, line } from 'd3-shape'
export default {
  name: 'StackedAreaChart',
  components: {
    TextAxis,
    VerticalAxis
  },
  props: {
    data: {
      type: Array,
      default () { return []}
    },
    animation: {
      type: Boolean,
      default: true
    },
    animationDuration: {
      type: Number,
      default: 3000
    },
    animationOptions: {
      type: Object,
      default () {
        return {
          delay: 0,
          easing: 'Bounce',
          easingEdge: 'Out'
        }
      }
    },
    legends: {
      type: Array,
      default () { return []}
    }
  },
  data () {
    return {
      width: 1000,
      height: 500,
      values: []
    }
  },
  mounted () {
    this.values = this.getValues(this.data)
    this.resize()
    window.addEventListener('resize', this.resize)
  },
  methods: {
    resize () {
      this.width = this.$el.offsetWidth
      this.height = this.$el.offsetHeight
    },
    getValues(data) {
      let values = []
      for (let i = 0; i < data.length; i++) {
        let previous = 0
        for (let j = 0; j < data[i].data.length; j++) {
          let next = previous + data[i].data[j].value
          values[j] = values[j] || []
          values[j].push({
            name: data[i].data[j].name,
            value: data[i].data[j].value,
            date: data[i].year,
            values: [
              previous,
              next
            ]
          })
          previous = next
        }
      }
      return values
    }
  },
  watch: {
    data (newData) {
      this.values = this.getValues(newData)
    }
  },
  computed: {
    style () {
      return { height: `${this.height}px`, width: `${this.width}px` }
    },
    scale () {
      return Math.sqrt(this.height / 500)
    },
    max () {
      return Math.max(...this.data.map(d => d.data.reduce((acc, cv) => acc + cv.value, 0)))
    },
    horizentalAxisOffset () {
      return { top: 0, left: 0, bottom: this.height * .1, right: 0 }
    },
    verticalAxisOffset () {
      return { top: 0, left: 0, bottom: 0, right: this.width * .9 }
    },
    colorScale () {
      return scaleOrdinal()
        .range(['#bbb', '#999', '#faa795'])
        .domain([0, 1, 2])
    },
    lineColorScale () {
      return scaleOrdinal()
        .range(['#666', '#000','#f56043'])
        .domain([0, 1, 2])
    },
    scaleLinearY () {
      return scaleLinear()
        .domain([0, Math.max(...this.values.map(d => Math.max(...d.map(v => v.values[1]))))])
        .rangeRound([this.height * .95, this.height * .15])
    },
    scaleLinearX () {
      return scaleLinear()
        .domain([Math.min(...this.data.map(d => d.year)), Math.max(...this.data.map(d => d.year))])
        .rangeRound([0, this.width * .9])
    },
    pointScale () {
      return scalePoint()
        .rangeRound([0, this.width * .9])
        .domain(this.data.map(d => d.year))
    },
    lines () {
      return this.values.map(v => {
        return line().defined(d => d)
          .x(d => this.scaleLinearX(d.date))
          .y(d => this.scaleLinearY(d.values[1]))(v)
      })
    },
    zones () {
      return this.values.map(v => {
        return area()
          .x(d => this.scaleLinearX(d.date))
          .y0(d => this.scaleLinearY(d.values[0]))
          .y1(d => this.scaleLinearY(d.values[1]))(v)
      })
    }
  },
  beforeDestroy () {
    window.removeEventListener('resize', this.resize)
  }
}
</script>

<style>
.hc-axis-tick:first-child {
  text-anchor: start;
}
.hc-axis-tick:last-child {
  text-anchor: end;
}
</style>
